<template>
  <div class="row">
    <div class="flex xs12">
      <va-card title="代投成功率">
        <div class="row align--center mb-2">
          <div class="flex md3 xs12">
            <va-select
              label="时间范围"
              v-model="filters.periodPreset"
              :options="periodPresetOptions"
              key-by="value"
              @input="onPeriodPresetChange"
            />
          </div>
          <div class="flex md3 xs12">
            <va-select
              label="按月份"
              v-model="filters.yearMonth"
              :options="monthOptions"
              key-by="value"
              searchable
              clearable
              @input="onYearMonthChange"
            />
          </div>
          <div class="flex md3 xs12">
            <va-date-picker
              label="自定义日期区间"
              :config="{ mode: 'range' }"
              v-model="filters.dateRange"
              @input="onDateRangeChange"
            />
          </div>
          <div class="flex md3 xs12">
            <va-select
              label="ATS 站点"
              v-model="filters.site"
              :options="siteOptions"
              key-by="value"
              searchable
              clearable
            />
          </div>
        </div>

        <div class="row align--center mb-2">
          <div class="flex md3 xs12">
            <va-select
              label="投递状态"
              v-model="filters.status"
              :options="statusOptions"
              key-by="value"
              clearable
            />
          </div>
          <div class="flex md3 xs12">
            <va-select
              label="Welcome 初投"
              v-model="filters.welcomeFilter"
              :options="welcomeFilterOptions"
              key-by="value"
              clearable
            />
          </div>
          <div class="flex md3 xs12">
            <va-input
              v-model="filters.uid"
              label="用户 UID"
              placeholder="精确匹配 uid"
              removable
            />
          </div>
          <div class="flex md3 xs12 filter-actions">
            <va-button color="primary" :loading="loading" @click="loadStats">查询</va-button>
            <va-button flat color="secondary" @click="resetFilters">重置</va-button>
          </div>
        </div>

        <p class="stats-note mb-2" v-if="activeFilterSummary">
          当前筛选：{{ activeFilterSummary }}
        </p>

        <div class="row mb-2" v-if="overall">
          <div class="flex md3 xs6">
            <va-card color="success" stripe>
              <div class="stats-card">
                <div class="stats-card__label">成功率</div>
                <div class="stats-card__value">{{ formatRate(overall.successRate) }}</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card stripe>
              <div class="stats-card">
                <div class="stats-card__label">总投递</div>
                <div class="stats-card__value">{{ overall.total }}</div>
                <div class="stats-card__breakdown">成功+投递中+失败+重试耗尽+草稿+已被替代</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card color="success" stripe>
              <div class="stats-card">
                <div class="stats-card__label">成功</div>
                <div class="stats-card__value">{{ overall.success }}</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card color="warning" stripe>
              <div class="stats-card">
                <div class="stats-card__label">投递中</div>
                <div class="stats-card__value">{{ overall.processing }}</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card color="danger" stripe>
              <div class="stats-card">
                <div class="stats-card__label">失败</div>
                <div class="stats-card__value">{{ overall.failed }}</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card color="danger" stripe>
              <div class="stats-card">
                <div class="stats-card__label">重试耗尽</div>
                <div class="stats-card__value">{{ overall.retryExhausted }}</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card color="gray" stripe>
              <div class="stats-card">
                <div class="stats-card__label">草稿</div>
                <div class="stats-card__value">{{ overall.draft || 0 }}</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card color="gray" stripe>
              <div class="stats-card">
                <div class="stats-card__label">已被替代</div>
                <div class="stats-card__value">{{ overall.superseded || 0 }}</div>
              </div>
            </va-card>
          </div>
          <div class="flex md3 xs6">
            <va-card :color="rateColor(overall.retrySuccessRate)" stripe>
              <div class="stats-card">
                <div class="stats-card__label">重试成功率</div>
                <div class="stats-card__value">{{ formatRate(overall.retrySuccessRate) }}</div>
                <div class="stats-card__breakdown">先失败后重试成功 {{ overall.retrySuccess || 0 }} / 重试耗尽 {{ overall.retryExhausted || 0 }}</div>
              </div>
            </va-card>
          </div>
        </div>

        <p class="stats-note mb-2">
          成功率 = 成功 / (成功 + 失败 + 重试耗尽)，不含投递中、草稿及已被替代的记录。总投递 = 成功 + 投递中 + 失败 + 重试耗尽 + 草稿 + 已被替代。
          重试成功率 = 先失败后重试成功的岗位数 / (先失败后重试成功的岗位数 + 重试耗尽数)，衡量重试机制本身的挽回效果。
        </p>

        <div class="stats-table-wrapper" @click="handleSortClick">
          <va-data-table
            :fields="fields"
            :data="sortedSiteStats"
            :loading="loading"
            api-mode
            no-pagination
            no-data-label="暂无代投数据"
          >
            <template slot="successRate" slot-scope="props">
              <va-badge class="success-rate-badge" :color="rateColor(props.rowData.successRate)">
                {{ formatRate(props.rowData.successRate) }}
              </va-badge>
            </template>
            <template slot="retrySuccessRate" slot-scope="props">
              <va-badge class="success-rate-badge" :color="rateColor(props.rowData.retrySuccessRate)">
                {{ formatRate(props.rowData.retrySuccessRate) }}
              </va-badge>
            </template>
          </va-data-table>
        </div>
      </va-card>
    </div>
  </div>
</template>

<script>
import autoApplyStatsApi from '@/api/autoApplyStats'

const PERIOD_PRESETS = {
  ALL: 'all',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  LAST_3_MONTHS: 'last_3_months',
  LAST_6_MONTHS: 'last_6_months',
}

function startOfDay (date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfDay (date) {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

function toUnix (date) {
  return Math.floor(date.getTime() / 1000)
}

function selectValue (value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
    return value.value
  }
  return value
}

function parseDateRangeValue (dateRange) {
  if (!dateRange) {
    return null
  }
  if (Array.isArray(dateRange) && dateRange.length === 2) {
    return dateRange
  }
  if (typeof dateRange === 'string') {
    const parts = dateRange.split(' to ')
    if (parts.length === 2) {
      return parts
    }
  }
  return null
}

function parseYmd (ymd) {
  const [year, month, day] = ymd.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function defaultFilters () {
  return {
    periodPreset: PERIOD_PRESETS.ALL,
    yearMonth: null,
    dateRange: null,
    site: null,
    status: null,
    welcomeFilter: null,
    uid: '',
  }
}

export default {
  name: 'AutoApplySuccessRate',
  data () {
    return {
      loading: false,
      overall: null,
      siteStats: [],
      filters: defaultFilters(),
      availableSites: [],
      availableMonths: [],
      periodPresetOptions: [
        { text: '全部时间', value: PERIOD_PRESETS.ALL },
        { text: '本月', value: PERIOD_PRESETS.THIS_MONTH },
        { text: '上月', value: PERIOD_PRESETS.LAST_MONTH },
        { text: '近 3 个月', value: PERIOD_PRESETS.LAST_3_MONTHS },
        { text: '近 6 个月', value: PERIOD_PRESETS.LAST_6_MONTHS },
      ],
      statusOptions: [
        { text: '投递中', value: 0 },
        { text: '投递成功', value: 1 },
        { text: '投递失败', value: 2 },
        { text: '重试耗尽', value: 3 },
        { text: '草稿', value: -1 },
        { text: '已被替代', value: 4 },
      ],
      welcomeFilterOptions: [
        { text: '仅 Welcome 初投', value: 1 },
        { text: '排除 Welcome 初投', value: 0 },
      ],
      activeFilterSummary: '',
      sortKey: 'successRate',
      sortDirection: 'desc',
    }
  },
  computed: {
    monthOptions () {
      return this.availableMonths.map(month => ({ text: month, value: month }))
    },
    siteOptions () {
      return this.availableSites.map(site => ({ text: site, value: site }))
    },
    sortedSiteStats () {
      const key = this.sortKey
      const direction = this.sortDirection
      const multiplier = direction === 'asc' ? 1 : -1

      return [...this.siteStats].sort((a, b) => {
        if (key === 'site') {
          return multiplier * String(a.site || '').localeCompare(String(b.site || ''), 'zh-CN')
        }

        const left = Number(a[key]) || 0
        const right = Number(b[key]) || 0
        return multiplier * (left - right)
      })
    },
    fields () {
      return [
        {
          name: 'site',
          title: () => this.renderSortTitle('site', 'ATS 站点'),
          width: '15%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: '__slot:successRate',
          title: () => this.renderSortTitle('successRate', '成功率'),
          width: '12%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: 'total',
          title: () => this.renderSortTitle('total', '总投递'),
          width: '12%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: 'success',
          title: () => this.renderSortTitle('success', '成功'),
          width: '12%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: 'processing',
          title: () => this.renderSortTitle('processing', '投递中'),
          width: '12%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: 'failed',
          title: () => this.renderSortTitle('failed', '失败'),
          width: '12%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: 'retryExhausted',
          title: () => this.renderSortTitle('retryExhausted', '重试耗尽'),
          width: '12%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: 'draft',
          title: () => this.renderSortTitle('draft', '草稿'),
          width: '10%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: 'superseded',
          title: () => this.renderSortTitle('superseded', '已被替代'),
          width: '10%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
        {
          name: '__slot:retrySuccessRate',
          title: () => this.renderSortTitle('retrySuccessRate', '重试成功率'),
          width: '12%',
          titleClass: 'text-center sortable-header-cell',
          dataClass: 'text-center',
        },
      ]
    },
  },
  created () {
    this.loadFilterOptions()
    this.loadStats()
  },
  methods: {
    loadFilterOptions () {
      autoApplyStatsApi.getFilterOptions()
        .then(response => {
          this.availableSites = response.sites || []
          this.availableMonths = response.months || []
        })
        .catch(() => {
          this.availableSites = []
          this.availableMonths = []
        })
    },
    onPeriodPresetChange () {
      this.filters.yearMonth = null
      this.filters.dateRange = null
    },
    onYearMonthChange () {
      if (selectValue(this.filters.yearMonth)) {
        this.filters.periodPreset = PERIOD_PRESETS.ALL
        this.filters.dateRange = null
      }
    },
    onDateRangeChange () {
      if (parseDateRangeValue(this.filters.dateRange)) {
        this.filters.periodPreset = PERIOD_PRESETS.ALL
        this.filters.yearMonth = null
      }
    },
    buildQueryParams () {
      const params = {}
      const summary = []
      const periodPreset = selectValue(this.filters.periodPreset)
      const yearMonth = selectValue(this.filters.yearMonth)
      const site = selectValue(this.filters.site)
      const status = selectValue(this.filters.status)
      const welcomeFilter = selectValue(this.filters.welcomeFilter)
      const dateRange = parseDateRangeValue(this.filters.dateRange)

      if (dateRange) {
        params.startTime = toUnix(startOfDay(parseYmd(dateRange[0])))
        params.endTime = toUnix(endOfDay(parseYmd(dateRange[1])))
        summary.push(`自定义区间 ${dateRange[0]} ~ ${dateRange[1]}`)
      } else if (yearMonth) {
        params.yearMonth = yearMonth
        summary.push(`月份 ${yearMonth}`)
      } else {
        const range = this.resolvePeriodPresetRange(periodPreset)
        if (range) {
          params.startTime = range.startTime
          params.endTime = range.endTime
          summary.push(this.periodPresetLabel(periodPreset))
        }
      }

      if (site) {
        params.site = site
        summary.push(`ATS ${site}`)
      }
      if (status !== null && status !== undefined && status !== '') {
        params.status = status
        summary.push(`状态 ${this.statusLabel(status)}`)
      }
      if (welcomeFilter !== null && welcomeFilter !== undefined && welcomeFilter !== '') {
        params.welcomeFilter = welcomeFilter
        summary.push(welcomeFilter === 1 ? '仅 Welcome' : '排除 Welcome')
      }
      if (this.filters.uid && this.filters.uid.trim()) {
        params.uid = this.filters.uid.trim()
        summary.push(`UID ${params.uid}`)
      }

      this.activeFilterSummary = summary.length ? summary.join(' · ') : '全部数据'
      return params
    },
    resolvePeriodPresetRange (preset) {
      const now = new Date()
      if (preset === PERIOD_PRESETS.ALL) {
        return null
      }
      if (preset === PERIOD_PRESETS.THIS_MONTH) {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(now)) }
      }
      if (preset === PERIOD_PRESETS.LAST_MONTH) {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const end = new Date(now.getFullYear(), now.getMonth(), 0)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(end)) }
      }
      if (preset === PERIOD_PRESETS.LAST_3_MONTHS) {
        const start = new Date(now.getFullYear(), now.getMonth() - 2, 1)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(now)) }
      }
      if (preset === PERIOD_PRESETS.LAST_6_MONTHS) {
        const start = new Date(now.getFullYear(), now.getMonth() - 5, 1)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(now)) }
      }
      return null
    },
    loadStats () {
      this.loading = true
      const params = this.buildQueryParams()
      autoApplyStatsApi.getSuccessRate(params)
        .then(response => {
          this.overall = response.overall
          this.siteStats = (response.siteStats || []).map(row => ({
            ...row,
            draft: row.draft || 0,
            superseded: row.superseded || 0,
            retrySuccess: row.retrySuccess || 0,
            retrySuccessRate: row.retrySuccessRate || 0,
          }))
        })
        .catch(error => {
          this.$vaToast.init({ message: error.msg || '加载代投成功率失败', color: 'danger' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    resetFilters () {
      this.filters = defaultFilters()
      this.loadStats()
    },
    periodPresetLabel (preset) {
      const option = this.periodPresetOptions.find(item => item.value === preset)
      return option ? option.text : '全部时间'
    },
    statusLabel (status) {
      const option = this.statusOptions.find(item => item.value === status)
      return option ? option.text : status
    },
    formatRate (rate) {
      if (rate === null || rate === undefined) {
        return '0.00%'
      }
      return `${Number(rate).toFixed(2)}%`
    },
    rateColor (rate) {
      if (rate >= 70) {
        return 'success'
      }
      if (rate >= 40) {
        return 'warning'
      }
      return 'danger'
    },
    renderSortTitle (key, label) {
      const isActive = this.sortKey === key
      const icon = isActive
        ? (this.sortDirection === 'asc' ? 'fa-caret-up' : 'fa-caret-down')
        : 'fa-sort'
      return `<span class="sortable-header" data-sort="${key}">${label}<span class="fa ${icon} sort-icon"></span></span>`
    },
    handleSortClick (event) {
      const target = event.target.closest('[data-sort]')
      if (!target) {
        return
      }
      const key = target.dataset.sort
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sortKey = key
      this.sortDirection = 'asc'
    },
  },
}
</script>

<style scoped>
.stats-card {
  padding: 0.5rem 0;
  text-align: center;
}

.stats-card__label {
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 0.95;
}

.stats-card__value {
  font-size: 2rem;
  font-weight: 700;
  margin-top: 0.25rem;
  letter-spacing: 0.02em;
}

.stats-card__breakdown {
  font-size: 0.7rem;
  opacity: 0.75;
  margin-top: 0.25rem;
}

.stats-note {
  color: #6c757d;
  font-size: 0.85rem;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  padding-bottom: 0.25rem;
}
</style>

<style>
.stats-table-wrapper .sortable-header-cell {
  color: #6c7fee;
  cursor: pointer;
  user-select: none;
}

.stats-table-wrapper .sortable-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.stats-table-wrapper .sort-icon {
  font-size: 0.625rem;
  opacity: 0.85;
}

.stats-table-wrapper .success-rate-badge {
  font-size: 0.95rem;
  font-weight: 700;
  padding: 0.35rem 0.75rem;
}
</style>
